import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import AdminProfileMenu from "../../components/AdminProfileMenu.jsx";
import AdminNotifications from "../../components/AdminNotifications.jsx";
import { useNavigate } from "react-router-dom";
import {
  Users,
  ShoppingCart,
  DollarSign,
  BarChart3,
  AlertCircle,
  TrendingUp,
  Activity,
  PackagePlus,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import AdminSidebar from "../../components/AdminSidebar.jsx";

export default function AdminPanel() {
  const [stats, setStats] = useState({
  totalUsers: null,
  totalOrders: null,
  newOrders: null,
  totalFunds: null,
  totalRevenue: null,
  reports: null,
});

  const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [darkMode, setDarkMode] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const panelRef = useRef(null);

  // ✅ Fetch overview stats from backend
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const res = await axios.get("https://api.speedfollowinfo.com/api/admin/overview");
        if (res.data.success) {
          setStats({
            totalUsers: res.data.totalUsers,
            totalOrders: res.data.totalOrders,
            newOrders: res.data.newOrders,
            totalFunds: res.data.totalFunds,
            totalRevenue: res.data.totalRevenue,
            reports: res.data.reports,
          });
        }
      } catch (err) {
        console.error("❌ Admin Overview Fetch Failed:", err);
      }
    };

    fetchOverview();
    const interval = setInterval(fetchOverview, 60000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Dummy chart data (for visualization)
  useEffect(() => {
    setChartData([
      { name: "Jan", users: 200, orders: 300, revenue: 12000 },
      { name: "Feb", users: 250, orders: 320, revenue: 14000 },
      { name: "Mar", users: 280, orders: 360, revenue: 15500 },
      { name: "Apr", users: 310, orders: 410, revenue: 17000 },
      { name: "May", users: 400, orders: 450, revenue: 18000 },
      { name: "Jun", users: 420, orders: 490, revenue: 20000 },
    ]);
  }, []);

  // ✅ Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ Theme toggle
  const themeClasses = darkMode
    ? "bg-[#0b0f1a] text-white"
    : "bg-gray-100 text-black";

  // ✅ Handle Logout & Account navigation (Admin-safe)
const handleLogout = () => {
  try {
    // 🧹 শুধু admin সম্পর্কিত key মুছবে
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");

    // ❌ user token একদম স্পর্শ করবে না
    // 🚪 নিরাপদভাবে redirect করবে
    window.location.replace("/admin-login");
  } catch (err) {
    console.error("Admin logout error:", err);
    // fallback redirect
    window.location.href = "/admin-login";
  }
};

// ✅ Handle Account navigation
const handleAccount = () => {
  navigate("/admin/account"); // 🔐 Go to Admin Account Page
};

// ✅ Main Return Section
return (
  <div className={`flex min-h-screen transition-colors duration-500 ${themeClasses}`}>
    <AdminSidebar darkMode={darkMode} />
      {/* ✅ Main Dashboard */}
      <div className="flex-1 p-6 md:p-10 overflow-y-auto" ref={panelRef}>
        {/* Topbar */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 size={26} /> Admin Dashboard Analytics
          </h1>

          {/* Notifications + Profile */}
          <div className="flex items-center gap-6">
            <AdminNotifications />
            <AdminProfileMenu
              showProfile={showProfile}
              setShowProfile={setShowProfile}
              darkMode={darkMode}
              setDarkMode={setDarkMode}
              onAccount={handleAccount} // ✅ New prop for Account button
              onLogout={handleLogout} // ✅ Logout works same
            />
          </div>
        </div>

        {/* ✅ Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 mb-10">
          {/* Total Users */}
          <div
            onClick={() => navigate("/admin/users")}
            className={`p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition cursor-pointer ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <Users size={30} className="text-[#6c63ff] mb-2" />
            <h2 className="text-lg font-semibold">Total Users</h2>
            <p className="text-3xl font-bold text-[#ffd700] mt-1">
  {stats.totalUsers === null ? "Loading 🕦" : stats.totalUsers}
</p>
          </div>

          {/* Total Orders */}
          <div
            onClick={() => navigate("/admin/orders")}
            className={`cursor-pointer p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <ShoppingCart size={30} className="text-[#6c63ff] mb-2" />
            <h2 className="text-lg font-semibold text-[#f2a900]">
              Total Orders
            </h2>
            <p className="text-3xl font-bold text-[#ffd700] mt-1">
              {stats.totalOrders === null ? "Loading 🕦" : stats.totalOrders}
            </p>
          </div>

          {/* New Orders */}
          <div
            onClick={() => navigate("/admin/new-orders")}
            className={`p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition cursor-pointer ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <PackagePlus size={30} className="text-[#00e0ff] mb-2" />
            <h2 className="text-lg font-semibold">New Orders</h2>
            <p className="text-3xl font-bold text-[#00e0ff] mt-1">
              {stats.newOrders === null ? "Loading 🕦" : `+${stats.newOrders}`}
            </p>
          </div>

          {/* 💰 Total Funds */}
          <div
            onClick={() => navigate("/admin/total-funds")}
            className={`p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition cursor-pointer ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <DollarSign size={30} className="text-[#6c63ff] mb-2" />
            <h2 className="text-lg font-semibold">💰 Total Funds</h2>
            <p className="text-3xl font-bold text-[#ffd700] mt-1">
              {stats.totalFunds === null ? "Loading 🕦" : stats.totalFunds}
            </p>
          </div>

          {/* Reports */}
          <div
            onClick={() => navigate("/admin/reports")}
            className={`cursor-pointer p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <AlertCircle size={30} className="text-[#6c63ff] mb-2" />
            <h2 className="text-lg font-semibold">Reports</h2>
            <p className="text-3xl font-bold text-[#ffd700] mt-1">
              {stats.reports === null ? "Loading 🕦" : stats.reports}
            </p>
          </div>

          {/* 📈 Total Revenue */}
          <div
            onClick={() => navigate("/admin/revenue-details")}
            className={`p-5 rounded-xl shadow-lg flex flex-col items-center hover:scale-105 transition cursor-pointer ${
              darkMode ? "bg-[#111827]" : "bg-white"
            }`}
          >
            <TrendingUp size={30} className="text-[#00ff88] mb-2" />
            <h2 className="text-lg font-semibold">Total Revenue</h2>
            <p className="text-3xl font-bold text-[#22c55e] mt-1">
              ৳{stats.totalRevenue === null
  ? "Loading 🕦"
  : `৳${stats.totalRevenue.toLocaleString()}`}
            </p>
          </div>
        </div>

        {/* ✅ Line Chart */}
        <div
          className={`p-5 rounded-xl shadow-lg mb-8 transition ${
            darkMode ? "bg-[#111827]" : "bg-white"
          }`}
        >
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Activity size={18} /> Monthly Revenue Growth
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#222" : "#ccc"} />
              <XAxis dataKey="name" stroke={darkMode ? "#aaa" : "#333"} />
              <YAxis stroke={darkMode ? "#aaa" : "#333"} />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? "#111827" : "#fff",
                  border: "1px solid #ccc",
                  color: darkMode ? "#fff" : "#000",
                }}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#6c63ff"
                strokeWidth={3}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ✅ Bar Chart */}
        <div
          className={`p-5 rounded-xl shadow-lg transition ${
            darkMode ? "bg-[#111827]" : "bg-white"
          }`}
        >
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Users size={18} /> Users & Orders Analytics
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#222" : "#ccc"} />
              <XAxis dataKey="name" stroke={darkMode ? "#aaa" : "#333"} />
              <YAxis stroke={darkMode ? "#aaa" : "#333"} />
              <Tooltip
                contentStyle={{
                  backgroundColor: darkMode ? "#111827" : "#fff",
                  border: "1px solid #ccc",
                  color: darkMode ? "#fff" : "#000",
                }}
              />
              <Bar dataKey="users" fill="#6c63ff" radius={[4, 4, 0, 0]} />
              <Bar dataKey="orders" fill="#f2a900" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}